import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import type { ReactNode } from 'react'
import type { UserMe } from '../api/client'
import { useAuth } from '../features/auth/AuthProvider'
import { AccountProvider, useAccount } from '../features/auth/AccountProvider'
import { LoginPage } from '../features/auth/LoginPage'
import { AppShell } from '../components/AppShell'
import { HubProvider } from '../features/realtime/HubProvider'

// Route-level code splitting (fable-findings P10): each page ships as its own
// chunk, so first paint only loads the shell + the page being visited —
// react-grid-layout, for example, downloads only when the dashboard does.
// LoginPage stays eager; it IS the entry experience.
const DashboardPage = lazy(() =>
    import('../features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const GamesPage = lazy(() =>
    import('../features/games/GamesPage').then((m) => ({ default: m.GamesPage })),
)
const HistoryPage = lazy(() =>
    import('../features/history/HistoryPage').then((m) => ({ default: m.HistoryPage })),
)
const SettingsPage = lazy(() =>
    import('../features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const AdminPage = lazy(() =>
    import('../features/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
)
const EnginePage = lazy(() =>
    import('../features/engine/EnginePage').then((m) => ({ default: m.EnginePage })),
)

const RouteFallback = () => <div className="p-8 text-ink-500">Loading…</div>

// Re-keyed per account: server-side group membership is fixed when a hub
// connection is made, so a switch tears both hubs down and re-dials them for
// the new account — and remounts everything below, so no page keeps state
// from the account it was rendered for.
const AccountScopedHubs = ({ children }: { children: ReactNode }) => {
    const { active } = useAccount()
    return <HubProvider key={active.id}>{children}</HubProvider>
}

const AuthenticatedShell = ({ user, children }: { user: UserMe; children: ReactNode }) => (
    <AccountProvider user={user}>
        <AccountScopedHubs>
            <AppShell>
                <Suspense fallback={<RouteFallback />}>{children}</Suspense>
            </AppShell>
        </AccountScopedHubs>
    </AccountProvider>
)

const RequireAuth = ({ children }: { children: ReactNode }) => {
    const { user, loading } = useAuth()
    if (loading) return <div className="p-8 text-ink-500">Loading session…</div>
    if (!user) return <Navigate to="/login" replace />
    return <AuthenticatedShell user={user}>{children}</AuthenticatedShell>
}

const RequireAdmin = ({ children }: { children: ReactNode }) => {
    const { user, loading } = useAuth()
    if (loading) return <div className="p-8 text-ink-500">Loading session…</div>
    if (!user) return <Navigate to="/login" replace />
    if (!user.roles.includes('Admin')) return <Navigate to="/dashboard" replace />
    return <AuthenticatedShell user={user}>{children}</AuthenticatedShell>
}

export const AppRouter = () => (
    <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
            path="/"
            element={
                <RequireAuth>
                    <Navigate to="/dashboard" replace />
                </RequireAuth>
            }
        />
        <Route
            path="/dashboard"
            element={
                <RequireAuth>
                    <DashboardPage />
                </RequireAuth>
            }
        />
        <Route
            path="/games"
            element={
                <RequireAuth>
                    <GamesPage />
                </RequireAuth>
            }
        />
        <Route
            path="/history"
            element={
                <RequireAuth>
                    <HistoryPage />
                </RequireAuth>
            }
        />
        {/* /instances merged into /games — keep a redirect so old bookmarks still land somewhere. */}
        <Route path="/instances" element={<Navigate to="/games" replace />} />
        {/* Recordings and replay are admin-only operator tools living under
            /admin — keep redirects so old bookmarks still land somewhere. A
            non-admin following them falls through RequireAdmin to /dashboard. */}
        <Route path="/recordings" element={<Navigate to="/admin/recordings" replace />} />
        <Route path="/replay" element={<Navigate to="/admin/replay" replace />} />
        <Route
            path="/settings"
            element={
                <RequireAuth>
                    <SettingsPage />
                </RequireAuth>
            }
        />
        {/* Top-level admin-only Engine section (engine-metrics plan §3.1) —
            a metrics board, distinct from the /admin/engine ops tab. */}
        <Route
            path="/engine"
            element={
                <RequireAdmin>
                    <EnginePage />
                </RequireAdmin>
            }
        />
        <Route
            path="/admin/*"
            element={
                <RequireAdmin>
                    <AdminPage />
                </RequireAdmin>
            }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
)
